import { IVector2 } from './IVector2.js';
import { IVector3 } from './IVector3.js';
/**
 * Utility class for creating and manipulating `IVector4` objects.
 */
export class IVector4 {
    constructor(...args) {
        if (args.length === 0) {
            return { x: 0, y: 0, z: 0, w: 0 };
        }
        else if (args.length === 1) {
            const [value] = args;
            return { x: value, y: value, z: value, w: value };
        }
        else if (args.length === 2) {
            const [xyz, wzw] = args;
            if (IVector3.is(xyz) && typeof wzw === 'number') {
                return Object.assign(Object.assign({}, xyz), { w: wzw });
            }
            else if (typeof wzw === 'object' && IVector2.is(wzw)) {
                return Object.assign(Object.assign({}, xyz), { z: wzw.x, w: wzw.y });
            }
            else {
                throw Error('Invalid IVector4 constructor');
            }
        }
        else {
            const [x, y, z, w] = args;
            return { x: x, y: y, z: z, w: w };
        }
    }
    /**
     * Returns a new `IVector4` object with all components set to 0.
     * @returns Anonymous object `{ x: 0, y: 0, z: 0, w: 0 }`
     */
    static Zero() {
        return new IVector4(0, 0, 0, 0);
    }
    /**
     * Returns a new `IVector4` object with all components set to 1.
     * @returns Anonymous object `{ x: 1, y: 1, z: 1, w: 1 }`
     */
    static One() {
        return new IVector4(1, 1, 1, 1);
    }
    /**
     * Type guard to check if the provided object is applicable to the `IVector4` interface.
     * @returns Type assertion if the object is applicable to the IVector4.
     */
    static is(value) {
        const vector = value;
        return 'x' in vector && 'y' in vector && 'z' in vector && 'w' in vector && typeof vector.x === 'number' && typeof vector.y === 'number' && typeof vector.z === 'number' && typeof vector.w === 'number';
    }
    /**
     * Creates a new `IVector4` object as a verbose copy of the provided vector.
     * @returns A new `IVector4` object.
     */
    static copy(vector) {
        return new IVector4(vector.x, vector.y, vector.z, vector.w);
    }
    /**
     * Copies the values from one `IVector4` object to another.
     * @note This method directly modifies the `result` vector.
     */
    static copy_to_ref(vector, result) {
        result.x = vector.x;
        result.y = vector.y;
        result.z = vector.z;
        result.w = vector.w;
        return result;
    }
    /**
     * Directly sets the values to a `IVector4` object.
     * @note This method directly modifies the `target` vector.
     */
    static set(target, x, y, z, w) {
        target.x = x;
        target.y = y;
        target.z = z;
        target.w = w;
        return target;
    }
    /**
     * Calculates the magnitude (length) of the given `IVector4`.
     * @returns The magnitude of the vector.
     */
    static magnitude(vector) {
        return Math.sqrt(IVector4.magnitude_squared(vector));
    }
    /**
     * Calculates the squared magnitude of the given `IVector4`.
     * This method is useful for performance-sensitive situations.
     * @returns The squared magnitude of the vector.
     */
    static magnitude_squared(vector) {
        return Math.pow(vector.x, 2) + Math.pow(vector.y, 2) + Math.pow(vector.z, 2) + Math.pow(vector.w, 2);
    }
    /**
     * Normalizes the given `IVector4` object and returns a new object with the normalized values.
     * @returns A new `IVector4` object.
     */
    static normalize(vector) {
        const magnitude = IVector4.magnitude(vector);
        if (magnitude === 0) {
            // TODO: Add a verbose warning.
        }
        return new IVector4(vector.x / magnitude, vector.y / magnitude, vector.z / magnitude, vector.w / magnitude);
    }
    /**
     * Calculates the distance between two `IVector4` instances.
     * @returns The distance between vector1 and vector2.
     */
    static distance(vector1, vector2) {
        return Math.sqrt(IVector4.distance_squared(vector1, vector2));
    }
    /**
     * Calculates the squared distance between two `IVector4` instances.
     * This method is useful for performance-sensitive situations.
     * @returns The squared distance between vector1 and vector2.
     */
    static distance_squared(vector1, vector2) {
        const dx = vector1.x - vector2.x;
        const dy = vector1.y - vector2.y;
        const dz = vector1.z - vector2.z;
        const dw = vector1.w - vector2.w;
        return Math.pow(dx, 2) + Math.pow(dy, 2) + Math.pow(dz, 2) + Math.pow(dw, 2);
    }
    /**
     * Scales an `IVector4` object by a scalar.
     * @returns A new `IVector4` object.
     */
    static scale(vector, scalar) {
        return new IVector4(vector.x * scalar, vector.y * scalar, vector.z * scalar, vector.w * scalar);
    }
    /**
     * Scales an IVector4 instance by a scalar in place, modifying its components directly.
     * @note This method directly modifies the `vector` instance.
     * @returns The modified `IVector4` object reference.
     */
    static scale_in_place(vector, scalar) {
        vector.x *= scalar;
        vector.y *= scalar;
        vector.z *= scalar;
        vector.w *= scalar;
        return vector;
    }
    /**
     * Checks if two `IVector4` objects are equal by comparing their components.
     * @returns True if the components of vector1 and vector2 are exactly equal.
     */
    static equals(vector1, vector2) {
        return vector1.x === vector2.x && vector1.y === vector2.y && vector1.z === vector2.z && vector1.w === vector2.w;
    }
    /**
     * Checks if two IVector4 instances are approximately equal using an epsilon.
     * This is useful for dealing with floating-point precision issues.
     * @returns True if the differences of the components are less than epsilon.
     */
    static equals_with_epsilon(vector1, vector2, epsilon) {
        return Math.abs(vector1.x - vector2.x) < epsilon &&
            Math.abs(vector1.y - vector2.y) < epsilon &&
            Math.abs(vector1.z - vector2.z) < epsilon &&
            Math.abs(vector1.w - vector2.w) < epsilon;
    }
    /**
     * Adds two `IVector4` objects together and returns a new object with the result.
     * @returns A new `IVector4` objects.
     */
    static add(vector1, vector2) {
        return new IVector4(vector1.x + vector2.x, vector1.y + vector2.y, vector1.z + vector2.z, vector1.w + vector2.w);
    }
    /**
     * Substracts the second `IVector4` object from the first and returns a new object with the result.
     * @returns A new `IVector4` objects.
     */
    static subtract(vector1, vector2) {
        return new IVector4(vector1.x - vector2.x, vector1.y - vector2.y, vector1.z - vector2.z, vector1.w - vector2.w);
    }
    /**
     * Calculates the dot product of two `IVector4` objects.
     * @returns The dot product of two vectors.
     */
    static dot(vector1, vector2) {
        return vector1.x * vector2.x + vector1.y * vector2.y + vector1.z * vector2.z + vector1.w * vector2.w;
    }
    /**
     * Multiplies the components of two `IVector4` objects and returns a new object with the result.
     * @returns A new `IVector4` objects.
     */
    static multiply(vector1, vector2) {
        return new IVector4(vector1.x * vector2.x, vector1.y * vector2.y, vector1.z * vector2.z, vector1.w * vector2.w);
    }
    /**
     * Divides the components of the first `IVector4` object by the components of the second.
     * @returns A new `IVector4` objects.
     */
    static divide(vector1, vector2) {
        return new IVector4(vector1.x / vector2.x, vector1.y / vector2.y, vector1.z / vector2.z, vector1.w / vector2.w);
    }
    /**
     * Returns a new `IVector4` object representing the component-wise minimum of two vectors.
     * @returns A new `IVector4` object.
     */
    static min(vector1, vector2) {
        return new IVector4(Math.min(vector1.x, vector2.x), Math.min(vector1.y, vector2.y), Math.min(vector1.z, vector2.z), Math.min(vector1.w, vector2.w));
    }
    /**
     * Returns a new `IVector4` object representing the component-wise maximum of two vectors.
     * @returns A new `IVector4` object.
     */
    static max(vector1, vector2) {
        return new IVector4(Math.max(vector1.x, vector2.x), Math.max(vector1.y, vector2.y), Math.max(vector1.z, vector2.z), Math.max(vector1.w, vector2.w));
    }
    /**
     * Linearly interpolates between two `IVector4` objects by the given amount.
     * @param a The start `IVector4`.
     * @param b The end `IVector4`.
     * @param t The interpolation factor `0 - 1`.
     * @returns A new `IVector4` object.
     */
    static lerp(a, b, t) {
        return IVector4.lerp_to_ref(a, b, t, new IVector4());
    }
    /**
     * Linearly interpolates between two `IVector4` objects by the given amount.
     * @param a The start `IVector4`.
     * @param b The end `IVector4`.
     * @param t The interpolation factor `0 - 1`.
     * @param ref The `IVector4` to store result in.
     * @returns ref parameter.
     */
    static lerp_to_ref(a, b, t, ref) {
        ref.x = a.x * (1 - t) + b.x * t;
        ref.y = a.y * (1 - t) + b.y * t;
        ref.z = a.z * (1 - t) + b.z * t;
        ref.w = a.w * (1 - t) + b.w * t;
        return ref;
    }
    /**
     * Converts an `IVector4` object to an array of numbers `[ x, y, z, w ]`.
     * @returns An array `[ number, number, number, number ]`.
     */
    static to_array(vector3) {
        return [vector3.x, vector3.y, vector3.z, vector3.w];
    }
    /**
     * Converts an `IVector4` object to a `string`.
     * @returns A string in the format `{ x: number, y: number, z: number, w: number }`.
     */
    static to_string(vector) {
        return `{ x: ${vector.x}, y: ${vector.y}, z: ${vector.z}, w: ${vector.w}`;
    }
}
//# sourceMappingURL=IVector4.js.map